import { queryOptions } from "@tanstack/react-query";

import { adminClient } from "@/lib/api/admin-client.js";
import type { PaginatedList } from "@/lib/api/envelopes.js";

export type Status = "DRAFT" | "PUBLISHED" | "ARCHIVED";
export type Difficulty = "BEGINNER" | "INTERMEDIATE" | "ADVANCED";
export type Importance = "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";

export type ScheduleRow = {
  publicId: string;
  name: string;
  description: string | null;
  difficulty: Difficulty;
  dailyMinutes: number;
  minRecitations: number;
  maxRecitations: number | null;
  status: Status;
  createdAt: string;
  _count?: { guidelines?: number; routines?: number };
};

export type GuidelineRow = {
  publicId: string;
  topic: string;
  guidance: string;
  importance: Importance;
  createdAt: string;
  schedule?: { publicId: string; name: string } | null;
};

export type RoutineRow = {
  publicId: string;
  dayNumber: number;
  scriptureSequence: string[];
  timing: string;
  notes: string | null;
  createdAt: string;
  schedule?: { publicId: string; name: string } | null;
};

export const dailyRecitationKeys = {
  all: ["admin-daily-recitation"] as const,
  lists: () => [...dailyRecitationKeys.all, "list"] as const,
  list: (owner: "schedules" | "guidelines" | "routines", f?: Record<string, unknown>) =>
    [...dailyRecitationKeys.lists(), owner, ...(f ? [f] : [])] as const,
  details: () => [...dailyRecitationKeys.all, "detail"] as const,
  detail: (owner: string, publicId: string) => [...dailyRecitationKeys.details(), owner, publicId] as const,
  schedules: (f?: Record<string, unknown>) => dailyRecitationKeys.list("schedules", f),
  guidelines: (f?: Record<string, unknown>) => dailyRecitationKeys.list("guidelines", f),
  routines: () => dailyRecitationKeys.list("routines"),
};

export function listSchedulesOptions(filters: { page?: number; limit?: number; status?: string; difficulty?: string } = {}) {
  const params: Record<string, string | number | undefined> = {
    page: filters.page ?? 1,
    limit: filters.limit ?? 20,
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.difficulty ? { difficulty: filters.difficulty } : {}),
  };
  return queryOptions({
    queryKey: dailyRecitationKeys.schedules(filters),
    queryFn: () => adminClient.get<PaginatedList<ScheduleRow>>("/admin/daily-recitation/schedules", params),
  });
}

export function listGuidelinesOptions(filters: { page?: number; limit?: number; scheduleId?: string } = {}) {
  const params: Record<string, string | number | undefined> = {
    page: filters.page ?? 1,
    limit: filters.limit ?? 20,
    ...(filters.scheduleId ? { scheduleId: filters.scheduleId } : {}),
  };
  return queryOptions({
    queryKey: dailyRecitationKeys.guidelines(filters),
    queryFn: () => adminClient.get<PaginatedList<GuidelineRow>>("/admin/daily-recitation/guidelines", params),
  });
}

export function listRoutinesOptions() {
  return queryOptions({
    queryKey: dailyRecitationKeys.routines(),
    queryFn: () => adminClient.get<{ data: RoutineRow[] }>("/admin/daily-recitation/routines"),
  });
}
